// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import artifactApi from './artifact.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchArtifactApi = createAsyncThunk(
    'itemSaga/artifact/fetchArtifactApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await artifactApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleArtifactPublishedStateApi = createAsyncThunk(
    'itemSaga/artifact/toggleArtifactPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await artifactApi.update(row.id, { is_ready: !row.is_ready });
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createArtifactApi = createAsyncThunk(
    'itemSaga/artifact/createArtifactApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await artifactApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeArtifactApi = createAsyncThunk(
    'itemSaga/artifact/storeArtifactApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const artifact = await artifactApi.store(values);
            alertSwal('success', 'Artifact created successfully!');

            return artifact;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showArtifactApi = createAsyncThunk(
    'itemSaga/artifact/showArtifactApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await artifactApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateArtifactApi = createAsyncThunk(
    'itemSaga/artifact/updateArtifactApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await artifactApi.update(id, values);
            alertSwal('success', 'Artifact updated successfully!');

            const { ranks } = state.itemSaga.artifactMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteArtifactApi = createAsyncThunk('itemSaga/artifact/deleteArtifactApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await artifactApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const artifactSlice = createSlice({
    name: 'itemSaga/artifact',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedArtifact(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchArtifactApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchArtifactApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleArtifactPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleArtifactPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createArtifactApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createArtifactApi.rejected, () => {
                //
            });

        builder
            .addCase(storeArtifactApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeArtifactApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showArtifactApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showArtifactApi.rejected, () => {
                //
            });

        builder
            .addCase(updateArtifactApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateArtifactApi.rejected, () => {
                //
            });

        builder.addCase(deleteArtifactApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedArtifact } = artifactSlice.actions;
